import {
  AllAccountsCard,
  BankAccountFilterCard,
} from '@/components/accounts/BankAccountFilterCard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CategoryPicker } from '@/components/categories/CategoryPicker';
import { MerchantPicker } from '@/components/merchants/MerchantPicker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { useIsMobile } from '@/components/ui/use-mobile';
import { useBackBlocker } from '@/hooks/use-back-blocker';
import { useBankAccounts } from '@/hooks/use-bank-accounts';
import { useInstitutionLogos } from '@/hooks/use-institution-logos';
import { filterActions, filterStore } from '@/state/transactions-filter-state';
import { TransactionFilters, TransactionType } from '@polybank/shared';
import {
  ArrowUpDown,
  Building2,
  Calendar,
  DollarSign,
  Filter,
  RotateCcw,
  Search,
  Store,
  Tag,
} from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnapshot } from 'valtio';
import { TransactionFiltersFormItem } from './TransactionFiltersFormItem';
interface TransactionFiltersDialogProps {
  children: React.ReactNode;
}

export function TransactionFiltersDialog({
  children,
}: TransactionFiltersDialogProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const filterSnap = useSnapshot(filterStore);
  const { data: bankAccountsData } = useBankAccounts();
  const { getLogoForAccount } = useInstitutionLogos(bankAccountsData.rows);

  // Get selected account IDs
  const selectedAccountIds = useMemo(() => {
    return (filterSnap.filters.accountFilter || []) as string[];
  }, [filterSnap.filters.accountFilter]);

  // Check if all accounts are selected (no filter = all accounts)
  const isAllAccountsSelected = selectedAccountIds.length === 0;

  // Toggle account selection
  const toggleAccountSelection = useCallback(
    (accountId: string) => {
      const currentSelection = [...selectedAccountIds];
      const index = currentSelection.indexOf(accountId);

      if (index > -1) {
        currentSelection.splice(index, 1);
      } else {
        currentSelection.push(accountId);
      }

      filterActions.updateFilter(
        'accountFilter',
        currentSelection.length > 0 ? currentSelection : undefined
      );
    },
    [selectedAccountIds]
  );

  // Select all accounts (clear the filter)
  const selectAllAccounts = useCallback(() => {
    filterActions.updateFilter('accountFilter', undefined);
  }, []);

  const handleOpenChange = useCallback((open: boolean) => {
    filterActions.setFiltersOpen(open);
    if (!open) {
      // Reset to applied filters when closing without applying
      filterActions.resetFilters();
    }
  }, []);

  const handleApplyFilters = useCallback(() => {
    filterActions.applyFilters();
    filterActions.setFiltersOpen(false);
  }, []);

  const handleResetFilters = useCallback(() => {
    filterActions.clearAllFilters();
  }, []);

  // Back protection - show confirmation only when filters form has pending changes
  useBackBlocker({
    enabled: filterSnap.isFiltersOpen,
    onBack: () => {
      filterActions.resetFilters();
      filterActions.setFiltersOpen(false);
    },
    askConfirm: filterSnap.pendingChanges,
    confirmTitle: t('transactionFilters.discardChangesTitle'),
    confirmDescription: t('transactionFilters.discardChangesDescription'),
  });

  const updateFilter = useCallback(
    <K extends keyof TransactionFilters>(
      key: K,
      value: TransactionFilters[K]
    ) => {
      filterActions.updateFilter(key, value);
    },
    []
  );

  const onClear = useCallback(
    (id: keyof TransactionFilters) => {
      updateFilter(id, undefined);
    },
    [updateFilter]
  );

  const activeFiltersCount = useMemo(() => {
    const appliedFilters = filterSnap.appliedFilters;
    let count = 0;

    if (appliedFilters.amountRange?.min || appliedFilters.amountRange?.max)
      count++;
    if (appliedFilters.categories && appliedFilters.categories.length > 0)
      count++;
    if (
      appliedFilters.transactionTypes &&
      appliedFilters.transactionTypes.length > 0
    )
      count++;
    if (appliedFilters.merchants && appliedFilters.merchants.length > 0)
      count++;
    /**
     * on mobile these filters are not visible, on desktop they are visible
     * so we don't count it on desktop
     */
    if (isMobile) {
      // if (appliedFilters.searchQuery) count++;
      if (appliedFilters.dateRange?.from || appliedFilters.dateRange?.to)
        count++;

      if (
        appliedFilters.accountFilter &&
        appliedFilters.accountFilter.length > 0
      )
        count++;
    }

    // if (appliedFilters.tags.length > 0) count++;
    return count;
  }, [filterSnap.appliedFilters, isMobile]);

  return (
    <Dialog open={filterSnap.isFiltersOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <div className='relative'>
          {children}
          {activeFiltersCount > 0 && (
            <Badge
              variant='destructive'
              className='absolute -top-2 -right-2 w-5 h-5 p-0 text-xs flex items-center justify-center rounded-full'
            >
              {activeFiltersCount}
            </Badge>
          )}
        </div>
      </DialogTrigger>
      <DialogContent className='max-w-2xl max-h-[90vh] flex flex-col'>
        {/* Fixed Header */}
        <DialogHeader className='flex-shrink-0'>
          <DialogTitle className='flex items-center gap-2'>
            <Filter className='w-5 h-5' />
            {t('transactionFilters.title')}
          </DialogTitle>
          <DialogDescription>
            {t('transactionFilters.description')}
          </DialogDescription>
        </DialogHeader>

        {/* Scrollable Content */}
        <div className='flex-1 overflow-y-auto space-y-4 py-4 px-1 -mx-1'>
          {/* Bank Accounts */}
          {isMobile && (
            <TransactionFiltersFormItem
              id='accountFilter'
              label={t('transactionFilters.bankAccounts')}
              isDirty={selectedAccountIds.length > 0}
              onClear={selectAllAccounts}
              icon={Building2}
              accent='blue'
            >
              <div className='flex flex-wrap gap-2 p-2 -m-2'>
                <AllAccountsCard
                  selected={isAllAccountsSelected}
                  onToggle={selectAllAccounts}
                />
                {bankAccountsData.rows.map((account) => (
                  <BankAccountFilterCard
                    key={account.$id}
                    accountName={account.account_name}
                    logoUrl={getLogoForAccount(account)}
                    selected={selectedAccountIds.includes(account.$id!)}
                    onToggle={() => toggleAccountSelection(account.$id!)}
                  />
                ))}
              </div>
            </TransactionFiltersFormItem>
          )}
          {/* Description Search */}
          <TransactionFiltersFormItem
            id='searchQuery'
            label={t('transactionFilters.descriptionLabel')}
            isDirty={!!filterSnap.filters.searchQuery}
            onClear={onClear}
            icon={Search}
            accent='violet'
          >
            <div className='flex gap-2'>
              <Select
                value={filterSnap.filters.searchMode || 'contains'}
                onValueChange={(value) =>
                  updateFilter(
                    'searchMode',
                    value as 'contains' | 'notContains'
                  )
                }
              >
                <SelectTrigger className='w-[140px] flex-shrink-0'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='contains'>
                    {t('common.contains')}
                  </SelectItem>
                  <SelectItem value='notContains'>
                    {t('common.doesNotContain')}
                  </SelectItem>
                </SelectContent>
              </Select>
              <Input
                id='description'
                placeholder={t('transactionFilters.descriptionPlaceholder')}
                value={filterSnap.filters.searchQuery || ''}
                onChange={(e) => updateFilter('searchQuery', e.target.value)}
                className='flex-1'
              />
            </div>
          </TransactionFiltersFormItem>

          {/* Date Range */}
          <TransactionFiltersFormItem
            id='dateRange'
            label={t('transactionFilters.dateRange')}
            isDirty={
              !!(
                filterSnap.filters.dateRange?.from ||
                filterSnap.filters.dateRange?.to
              )
            }
            onClear={onClear}
            icon={Calendar}
            accent='orange'
          >
            <DateRangePicker
              value={filterSnap.filters.dateRange}
              onChange={(range) => updateFilter('dateRange', range)}
            />
          </TransactionFiltersFormItem>

          {/* Amount Range */}
          <TransactionFiltersFormItem
            id='amountRange'
            label={t('transactionFilters.amountRange')}
            isDirty={
              !!(
                filterSnap.filters.amountRange?.min ||
                filterSnap.filters.amountRange?.max
              )
            }
            onClear={onClear}
            icon={DollarSign}
            accent='emerald'
          >
            <div className='grid grid-cols-2 gap-3'>
              <div className='space-y-2'>
                <Label className='text-sm text-muted-foreground'>
                  {t('common.minimum')}
                </Label>
                <Input
                  type='number'
                  placeholder='0.00'
                  value={filterSnap.filters.amountRange?.min || ''}
                  onChange={(e) =>
                    updateFilter('amountRange', {
                      min: Number(e.target.value),
                      max: filterSnap.filters.amountRange?.max || 0,
                    })
                  }
                  min='0'
                  step='0.01'
                />
              </div>
              <div className='space-y-2'>
                <Label className='text-sm text-muted-foreground'>
                  {t('common.maximum')}
                </Label>
                <Input
                  type='number'
                  placeholder='1000.00'
                  value={filterSnap.filters.amountRange?.max || ''}
                  onChange={(e) =>
                    updateFilter('amountRange', {
                      min: filterSnap.filters.amountRange?.min || 0,
                      max: Number(e.target.value),
                    })
                  }
                  min='0'
                  step='0.01'
                />
              </div>
            </div>
          </TransactionFiltersFormItem>

          {/* Categories */}
          <TransactionFiltersFormItem
            id='categories'
            label={t('transactionFilters.categories')}
            isDirty={
              !!(
                filterSnap.filters.categories &&
                filterSnap.filters.categories.length > 0
              )
            }
            onClear={onClear}
            icon={Tag}
            accent='pink'
          >
            <CategoryPicker
              multiSelect
              selectedCategoryIds={
                (filterSnap.filters.categories || []) as string[]
              }
              onSelectionChange={(selected) =>
                updateFilter(
                  'categories',
                  selected.length > 0 ? selected : undefined
                )
              }
              placeholder={t('transactionFilters.selectCategories')}
            />
          </TransactionFiltersFormItem>

          {/* Merchants */}
          <TransactionFiltersFormItem
            id='merchants'
            label={t('transactionFilters.merchants')}
            isDirty={
              !!(
                filterSnap.filters.merchants &&
                filterSnap.filters.merchants.length > 0
              )
            }
            onClear={onClear}
            icon={Store}
            accent='violet'
          >
            <MerchantPicker
              selectedMerchants={
                (filterSnap.filters.merchants || []) as string[]
              }
              onSelectionChange={(selected) =>
                updateFilter(
                  'merchants',
                  selected.length > 0 ? selected : undefined
                )
              }
              placeholder={t('transactionFilters.selectMerchants')}
            />
          </TransactionFiltersFormItem>

          {/* Transaction Type */}
          <TransactionFiltersFormItem
            id='transactionTypes'
            label={t('transactionFilters.transactionType')}
            isDirty={
              !!(
                filterSnap.filters.transactionTypes &&
                filterSnap.filters.transactionTypes.length > 0
              )
            }
            onClear={onClear}
            icon={ArrowUpDown}
            accent='cyan'
          >
            <Select
              value={filterSnap.filters.transactionTypes?.join(', ') || 'all'}
              onValueChange={(value) => {
                if (value === 'all') {
                  updateFilter('transactionTypes', undefined);
                } else
                  updateFilter('transactionTypes', [value as TransactionType]);
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='all'>
                  {t('transactions.allTransactions')}
                </SelectItem>
                <SelectItem value='DEBIT'>
                  {t('transactions.expensesOnlyFilter')}
                </SelectItem>
                <SelectItem value='CREDIT'>
                  {t('transactions.incomeOnlyFilter')}
                </SelectItem>
              </SelectContent>
            </Select>
          </TransactionFiltersFormItem>

          {/* Tags */}
          {/* <div className='space-y-3'>
            <Label>Tags</Label>
          </div> */}
        </div>

        <Separator className='flex-shrink-0' />

        {/* Fixed Footer */}
        <div className='flex-shrink-0 pt-4'>
          <div className='flex justify-between'>
            <Button
              type='button'
              variant='outline'
              onClick={handleResetFilters}
              className='gap-2'
            >
              <RotateCcw className='w-4 h-4' />
              {t('common.resetAll')}
            </Button>
            <div className='flex gap-2'>
              <Button
                type='button'
                variant='outline'
                onClick={() => filterActions.setFiltersOpen(false)}
              >
                {t('common.cancel')}
              </Button>
              <Button onClick={handleApplyFilters}>
                {t('transactionFilters.applyFilters')}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
